import React from "react";

/* RouteErrorBoundary
 *
 * Catches any uncaught render error inside the <Routes> tree so the user
 * gets a clear "Reload / Reset" affordance instead of a fully blank page.
 *
 * Specifically handles the production class of bug where a code-split chunk
 * fails to load because the CDN didn't ship it (Emergent's deploy pipeline
 * occasionally skips anonymous webpack chunks). Without this boundary, that
 * error propagates up and React unmounts the entire route → blank screen.
 */
export default class RouteErrorBoundary extends React.Component {
  state = { err: null };

  static getDerivedStateFromError(err) {
    return { err };
  }

  componentDidCatch(err) {
    // eslint-disable-next-line no-console
    console.error("[RouteErrorBoundary]", err);
  }

  hardReset = () => {
    try {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistrations().then((regs) => {
          Promise.all(regs.map((r) => r.unregister())).finally(() => {
            try {
              if (window.caches && caches.keys) {
                caches.keys().then((keys) =>
                  Promise.all(keys.map((k) => caches.delete(k))).finally(() => {
                    window.location.replace(
                      window.location.pathname + "?fresh=" + Date.now()
                    );
                  })
                );
              } else {
                window.location.replace(
                  window.location.pathname + "?fresh=" + Date.now()
                );
              }
            } catch {
              window.location.replace(
                window.location.pathname + "?fresh=" + Date.now()
              );
            }
          });
        });
      } else {
        window.location.replace(
          window.location.pathname + "?fresh=" + Date.now()
        );
      }
    } catch {
      window.location.reload();
    }
  };

  render() {
    if (!this.state.err) return this.props.children;

    const msg = String(this.state.err?.message || this.state.err || "");
    const looksLikeChunkBug =
      /chunk|loading.*failed|unexpected token '<'|dynamically imported/i.test(
        msg
      );

    return (
      <div
        className="min-h-[70vh] flex items-center justify-center px-6"
        data-testid="route-error-boundary"
      >
        <div className="max-w-md w-full border border-[#ff2a8e]/30 bg-black/60 backdrop-blur p-6 ">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#ff2a8e] mb-3">
            // VAULT SIGNAL LOST
          </div>
          <div className="font-display text-2xl text-white mb-2">
            This page didn&rsquo;t load cleanly.
          </div>
          <p className="text-sm text-white/70 mb-5 leading-relaxed">
            {looksLikeChunkBug
              ? "A recent deploy left a stale piece of the app cached in your browser. One tap of the button below will flush it and reload the page fresh."
              : "Something unexpected happened while opening this view. Try a fresh reload below — if it keeps happening, let V. Jones know."}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={this.hardReset}
              data-testid="route-error-reload-btn"
              className="px-4 py-2 bg-[#ff2a8e] hover:bg-[#ff2a8e]/90 text-black font-mono text-xs uppercase tracking-[0.2em] "
            >
              Reload &amp; Flush Cache
            </button>
            <a
              href="/"
              data-testid="route-error-home-btn"
              className="px-4 py-2 border border-white/20 hover:border-white/40 text-white/80 font-mono text-xs uppercase tracking-[0.2em] "
            >
              Home
            </a>
          </div>
          {msg && (
            <details className="mt-4 text-[10px] font-mono text-white/40">
              <summary className="cursor-pointer">debug info</summary>
              <pre className="mt-2 whitespace-pre-wrap break-all">{msg}</pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
